import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Church } from 'lucide-react';

export default function QuemSomos() {
  const { data: churchInfo, isLoading } = useQuery({
    queryKey: ['churchInfo'],
    queryFn: async () => {
      const info = await base44.entities.ChurchInfo.list();
      return info[0] || null;
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Cover Image */}
      <div 
        className={`${churchInfo?.cover_image_url ? 'h-56 md:h-72' : 'h-36 md:h-44'} bg-cover bg-center relative`}
        style={{ 
          backgroundImage: churchInfo?.cover_image_url 
            ? `url(${churchInfo.cover_image_url})` 
            : 'none',
          backgroundColor: 'var(--mbv-panel-deep)'
        }}
      >
        {churchInfo?.cover_image_url && <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="max-w-lg mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              {churchInfo?.name || 'Nossa Igreja'}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        <PageHeader title="Quem Somos" showBack={true} />

        {/* History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-sm mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Church className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Nossa História</h2>
          </div>
          <p className="text-gray-600 leading-relaxed whitespace-pre-line">
            {churchInfo?.history || 'Em breve teremos mais informações sobre nossa história.'}
          </p>
        </motion.div>

        {/* Mission, Vision, Values */}
        <div className="space-y-4">
          {churchInfo?.mission && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Target className="w-5 h-5 text-emerald-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Missão</h2>
              </div>
              <p className="text-gray-600 leading-relaxed">{churchInfo.mission}</p>
            </motion.div>
          )}

          {churchInfo?.vision && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#d8bd6828' }}>
                  <Eye className="w-5 h-5" style={{ color: 'var(--mbv-accent)' }} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Visão</h2>
              </div>
              <p className="text-gray-600 leading-relaxed">{churchInfo.vision}</p>
            </motion.div>
          )}

          {churchInfo?.values && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                  <Heart className="w-5 h-5 text-rose-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Valores</h2>
              </div>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{churchInfo.values}</p>
            </motion.div>
          )}
        </div>

        <div className="h-24" />
      </div>
    </div>
  );
}
